import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Register.css';
import { COLORS } from '../styles/colors';
import Dialog from '../components/Dialog'; // 다이얼로그 컴포넌트 import
import { registerUser } from '../utils/api'; // API 함수 import
// 로고 이미지 import
import logoImage from '../assets/tapgo-logo.png'; // 로고 파일명 변경

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userId: '',
    password: '',
    passwordConfirm: '',
    name: '',
    company: '',
    businessNumber: '',
    companyAddress: '', // 회사 주소 필드 추가
    birthdate: '',
    gender: '남',
    phoneNumber: '',
  });
  const [showDialog, setShowDialog] = useState(false); // 다이얼로그 상태 추가
  const [dialogMessage, setDialogMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [coordinates, setCoordinates] = useState(null); // 좌표값 저장
  const [addressLoading, setAddressLoading] = useState(false); // 주소 로딩 상태
  const [addressError, setAddressError] = useState(false); // 주소 에러 상태

  // 생년월일 형식화 함수
  const formatBirthdate = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 4)}.${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)}`;
    }
  };

  // 주소 API 호출 함수
  const fetchAddressCoordinates = async (address) => {
    if (!address || address.trim() === '') return;
    
    try {
      setAddressLoading(true);
      setAddressError(false);
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${BASE_URL}/api/geocode/public?address=${encodeURIComponent(address)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('주소 좌표를 가져오는데 실패했습니다.');
      }

      const data = await response.json();
      console.log('주소 좌표 응답:', data);
      
      if (data.status === 'success' && data.x && data.y) {
        setCoordinates(data);
        setAddressError(false);
      } else {
        setCoordinates(null);
        setAddressError(true);
      }
    } catch (error) {
      console.error('주소 좌표 가져오기 오류:', error);
      setCoordinates(null);
      setAddressError(true);
    } finally {
      setAddressLoading(false);
    }
  };

  // 주소 입력 후 일정 시간이 지나면 API 호출
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.companyAddress.trim().length > 5) {
        fetchAddressCoordinates(formData.companyAddress);
      }
    }, 1000); // 1초 딜레이

    return () => clearTimeout(timer);
  }, [formData.companyAddress]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'birthdate') {
      // 생년월일 필드의 경우 형식화 적용
      setFormData({
        ...formData,
        [name]: formatBirthdate(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // 주소 변경 시 좌표값 초기화
      if (name === 'companyAddress') {
        setCoordinates(null);
        setAddressError(false);
      }
    }
  };

  // API 요청을 위한 데이터 변환 함수
  const prepareApiData = () => {
    // 생년월일 형식 변환 (YYYY.MM.DD -> YYYY-MM-DD)
    const birthParts = formData.birthdate.split('.');
    const formattedBirth = birthParts.length === 3 
      ? `${birthParts[0]}-${birthParts[1]}-${birthParts[2]}`
      : formData.birthdate;

    // 좌표값이 있는 경우 추가
    const apiData = {
      middleUserID: formData.userId,
      middleBirth: formattedBirth,
      middleCompanyCode: '',
      middleBusinessNumber: formData.businessNumber,
      middleCompanyName: formData.company,
      middleCompanyAddress: formData.companyAddress, // 회사 주소 추가
      middleGender: formData.gender,
      middleName: formData.name,
      middlePassword: formData.password,
      middlePhone: formData.phoneNumber,
      userType: "middle"
    };

    // 좌표값이 있는 경우 추가
    if (coordinates && coordinates.x && coordinates.y) {
      apiData.latitude = coordinates.y;  // y 좌표는 위도(latitude)
      apiData.longitude = coordinates.x; // x 좌표는 경도(longitude)
    }

    return apiData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 유효성 검사
    if (formData.password !== formData.passwordConfirm) {
      setDialogMessage('비밀번호가 일치하지 않습니다.');
      setShowDialog(true);
      return;
    }

    // 필수 필드 검사
    const requiredFields = ['userId', 'password', 'name', 'company', 'businessNumber', 'companyAddress', 'birthdate', 'phoneNumber'];
    for (const field of requiredFields) {
      if (!formData[field]) {
        setDialogMessage(`${field === 'userId' ? '아이디' : 
                           field === 'password' ? '비밀번호' :
                           field === 'name' ? '이름' :
                           field === 'company' ? '업체명' :
                           field === 'businessNumber' ? '사업자번호' :
                           field === 'companyAddress' ? '회사 주소' :
                           field === 'birthdate' ? '생년월일' : '전화번호'}를 입력해주세요.`);
        setShowDialog(true);
        return;
      }
    }

    // 주소 좌표 검증
    if (addressError) {
      setDialogMessage('유효한 회사 주소를 입력해주세요.');
      setShowDialog(true);
      return;
    }

    try {
      setIsLoading(true);
      const apiData = prepareApiData();
      
      // API 호출
      const result = await registerUser(apiData);
      
      console.log('회원가입 성공:', result);
      
      // 성공 메시지 표시
      setDialogMessage('회원가입이 완료되었습니다.');
      setShowDialog(true);
      
      // 자동 리디렉션 제거 - 다이얼로그 닫을 때 리디렉션하도록 변경
    } catch (error) {
      console.error('회원가입 실패:', error);
      
      // 오류 메시지 표시
      setDialogMessage(error.message || '회원가입 중 오류가 발생했습니다.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    
    // 회원가입이 완료되었을 때만 로그인 페이지로 이동
    if (dialogMessage === '회원가입이 완료되었습니다.') {
      navigate('/login');
    }
  };

  return (
    <>
      <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
        <div className="appBarLogo">
          <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
        </div>
      </div>
      
      <div className="registerContainer">
        <h2 className="registerTitle">회원가입</h2>
        
        <form onSubmit={handleSubmit} className="registerForm">
          {/* 아이디와 업체명 */}
          <div className="formRow">
            <div className="formGroup">
              <label className="label">아이디</label>
              <input
                type="text"
                className="input"
                name="userId"
                value={formData.userId}
                onChange={handleChange}
                placeholder="아이디"
                required
              />
            </div>
            <div className="formGroup">
              <label className="label">업체명</label>
              <input
                type="text"
                className="input"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="업체명"
                required
              />
            </div>
          </div>
          
          {/* 비밀번호와 사업자번호 */}
          <div className="formRow">
            <div className="formGroup">
              <label className="label">비밀번호</label>
              <input
                type="password"
                className="input"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="비밀번호"
                required
              />
            </div>
            <div className="formGroup">
              <label className="label">사업자번호</label>
              <input
                type="text"
                className="input"
                name="businessNumber"
                value={formData.businessNumber}
                onChange={handleChange}
                placeholder="사업자번호"
                required
              />
            </div>
          </div>
          
          {/* 비밀번호 확인과 회사 주소 */}
          <div className="formRow">
            <div className="formGroup">
              <label className="label">비밀번호 확인</label>
              <input
                type="password"
                className="input"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleChange}
                placeholder="비밀번호 확인"
                required
              />
            </div>
            <div className="formGroup">
              <div className="labelWithHint">
                <label className="label">회사 주소</label>
                <span className="inputHint">예시: 서울특별시 송파구 올림픽로 300</span>
              </div>
              <input
                type="text"
                className="input"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleChange}
                placeholder="회사 주소"
                required
              />
              {addressLoading && <div className="address-loading">주소 검색 중...</div>}
              {addressError && (
                <div className="address-error">유효하지 않는 위치입니다</div>
              )}
              {coordinates && coordinates.x && coordinates.y && (
                <div className="coordinates-result">
                  <p>x: {coordinates.x}, y: {coordinates.y}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* 이름과 생년월일/성별 */}
          <div className="formRow">
            <div className="formGroup">
              <label className="label">이름</label>
              <input
                type="text"
                className="input"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="이름"
                required
              />
            </div>
            <div className="formGroup birthGenderContainer">
              <div className="birthGroup">
                <label className="label">생년월일</label>
                <input
                  type="text"
                  className="input birthdateInput"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleChange}
                  placeholder="YYYYMMDD"
                  maxLength="10"
                  required
                />
              </div>
              <div className="genderGroup">
                <label className="label">성별</label>
                <div className="genderButtons">
                  <button
                    type="button"
                    className={`genderButton ${formData.gender === '남' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, gender: '남' })}
                  >
                    남
                  </button>
                  <button
                    type="button"
                    className={`genderButton ${formData.gender === '여' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, gender: '여' })}
                  >
                    여
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* 전화번호와 회원가입 버튼 */}
          <div className="formRow lastRow">
            <div className="formGroup">
              <label className="label">전화번호</label>
              <input
                type="tel"
                className="input"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="전화번호"
                required
              />
            </div>
            <div className="formGroup">
              <label className="label" style={{ opacity: 0 }}>버튼</label>
              <button 
                type="submit" 
                className="registerButton"
                style={{ backgroundColor: COLORS.primary }}
                disabled={isLoading}
              >
                {isLoading ? '처리 중...' : '회원가입 완료'}
              </button>
            </div>
          </div>
          
          {/* 로그인 링크 */}
          <Link to="/login" className="loginLink">
            이미 계정이 있으신가요? 로그인하기
          </Link>
        </form>
      </div>

      {/* 커스텀 다이얼로그 대신 Dialog 컴포넌트 사용 */}
      <Dialog
        isOpen={showDialog}
        onClose={handleDialogClose}
        onConfirm={handleDialogClose}
        type="SUCCESS"
        message={dialogMessage}
      />
    </>
  );
};

export default Register;
